// IMPORTANT: Polyfills must be imported FIRST for Safari compatibility
import "./polyfills";

import React from "react";
import ReactDOM from "react-dom/client";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import { updateInstitutionProfile, updateMissionStatement } from "./helper/UserSettings";
import "react-bootstrap-typeahead/css/Typeahead.min.css";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet, // Outlet might be needed if App itself has sub-routes defined in its object
} from "react-router-dom";
import { APIGetUserInfo, APIExternalLogin } from "./helper/APIFunctions";
import App from "./App";
import { canRestoreVisuals, getDefaultPage } from "./helper/BusinessLogic";
import { UserInfo } from "./helper/Context";
import { useContext } from "react";

// Import all page components for route definitions
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import UseCasesPage from "./pages/UseCasesPage";
import PowerPointPage from "./pages/PowerPointPage";
import CreateVisualPage from "./pages/CreateVisualPage";
import PasswordResetPage from "./pages/PasswordResetPage";
import ProfilePage from "./pages/ProfilePage";
import PricingPage from "./pages/PricingPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import ContactPage from "./pages/ContactPage";
import DoiRequestPage from "./pages/DoiRequest.js";
import DashboardPage from "./pages/DashboardPage";
import DeletedVisualsPage from "./pages/DeletedVisualsPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import AdminPage from "./pages/AdminPage.js";
import BundlePage from "./pages/BundlesPage.js";
import GNYDM2023Page from "./pages/GNYDM2023Page.js";
import CreatePresentationPage from "./pages/CreatePresentationPage";
import ScrollytellingBuilderPage from "./pages/ScrollytellingBuilderPage";
import ScrollytellingPreviewPage from "./pages/ScrollytellingPreviewPage";
import TutorialsPage from "./pages/TutorialsPage";
import OrganizationInfoPage from "./pages/OrganizationInfoPage";
import OrganizationJoinPage from "./pages/OrganizationJoinPage";
import TaggedVisualsPage from "./pages/TaggedVisualsPage";
import AddTagsPage from "./pages/AddTagsPage";
import TagListPage from "./pages/TagListPage";
import GenerateExplainerPage from "./pages/GenerateExplainer";
import MolecularViewerPage from "./pages/MolecularViewerPage";
import OrganSegmentationPage from "./pages/OrganSegmentationPageV2";
import OrganSegmentationViewerPopout from "./pages/OrganSegmentationViewerPopout";
import FadeInWrapper from "./components/FadeInWrapper";
import ExamplesPage from "./pages/ExamplesPage";
import WelcomeScreen from "./pages/WelcomeScreen";
import AIFeatureGate from "./components/AIFeatureGate";
import PremiumFeatureGate from "./components/PremiumFeatureGate";
import OrbitDemoPage from "./pages/OrbitDemoPage";
import MyOrbitChannel from "./pages/MyOrbitChannel";
import OrbitPilotSignup from "./pages/OrbitPilotSignup";
import EmbedTestsPage from "./pages/EmbedTestsPage";
import EngagementDataExplorer from "./pages/EngagementDataExplorer";
import EngagementDataExplorerRU from "./pages/EngagementDataExplorerRU";
import ContentDashboard from "./pages/ContentDashboard";
import ChannelPreview from "./pages/ChannelPreview";
if (process.env.NODE_ENV === 'production') {
  console.log = () => {};
  console.info = () => {};
  console.debug = () => {};
  console.trace = () => {};
}
// Set a global flag for mobile detection *after* all imports.
window.isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

// Helper component for protected routes, using current UserInfo context instead of initialUserInfo
const ProtectedRoute = ({ children, pathName = 'Unknown', requireOrgPermissions = false, requireOrgAddVisuals = false }) => {
  const { userInfo } = useContext(UserInfo);
  
  // Show loading while userInfo is being initialized
  // This prevents components from trying to make API calls before userInfo is available
  if (userInfo === undefined) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  
  if (userInfo === null) {
    return <Navigate to="/home" replace />;
  }
  
  // Check for organization permissions if required
  if (requireOrgPermissions && !userInfo?.organization_permissions) {
    return <Navigate to="/home" replace />;
  }
  
  // Check for organization add visuals permission if required
  if (requireOrgAddVisuals && (!userInfo?.organization_permissions || !userInfo?.organization_permissions?.add_visuals)) {
    return <Navigate to="/home" replace />;
  }
  
  return children;
};

const startReactApp = (initialUserInfo) => {

  // Define routes here, using initialUserInfo
  const routesConfig = [
    {
      path: "/",
      element: <App userInfo={initialUserInfo} />, // App is the layout, gets userInfo
      children: [
        { index: true, element: <Navigate to={getDefaultPage(initialUserInfo)} replace /> }, // Default page logic
        { path: "home", element: <HomePage /> },
        { path: "welcome", element: <ProtectedRoute><WelcomeScreen /></ProtectedRoute> },
        { path: "examples", element: <ExamplesPage /> },
        { path: "about", element: <AboutPage /> },
        { path: "portal", element: <Navigate to="/home" replace /> }, // Alias
        { path: "usecases", element: <UseCasesPage /> },
        { path: "powerpoint", element: <PowerPointPage /> },
        {
          path: "create-presentation",
          element: (
            <ProtectedRoute>
              <PremiumFeatureGate featureName="Slide Deck Creator">
                <FadeInWrapper><CreatePresentationPage /></FadeInWrapper>
              </PremiumFeatureGate>
            </ProtectedRoute>
          ),
        },
        {
          path: "scrollytelling/builder",
          element: (
            <ProtectedRoute pathName="scrollytelling/builder">
              <PremiumFeatureGate featureName="Scrollytelling Builder">
                <FadeInWrapper><ScrollytellingBuilderPage /></FadeInWrapper>
              </PremiumFeatureGate>
            </ProtectedRoute>
          ),
        },
        {
          path: "scrollytelling/preview",
          element: <FadeInWrapper><ScrollytellingPreviewPage /></FadeInWrapper>,
        },
        { path: "passwordreset/:token", element: <PasswordResetPage /> },
        {
          path: "dashboard/*",
          element: <ProtectedRoute><DashboardPage /></ProtectedRoute>,
        },
        { path: "pricing", element: <PricingPage /> },
        { path: "privacy", element: <PrivacyPage /> },
        { path: "tos", element: <TermsPage /> },
        { path: "contact", element: <ContactPage /> },
        {
          path: "doi",
          element: (
            <PremiumFeatureGate featureName="DOI Request">
              <DoiRequestPage />
            </PremiumFeatureGate>
          ),
        },
        { path: "tutorials", element: <TutorialsPage /> },
        {
          path: "molecular-viewer",
          element: (
            <PremiumFeatureGate featureName="Live Molecular Viewer">
              <MolecularViewerPage />
            </PremiumFeatureGate>
          ),
        },
        {
          path: "organ-segmentation",
          element: (
            <AIFeatureGate featureName="Clinical Imaging Segmentation" featureKey="organ_segmentation">
              <OrganSegmentationPage />
            </AIFeatureGate>
          ),
        },
        { path: "organ-segmentation/viewer", element: <OrganSegmentationViewerPopout /> },
        {
          path: "organization/visuals",
          element: (
            <ProtectedRoute requireOrgPermissions={true}>
              <ProfilePage isOrg={true} />
            </ProtectedRoute>
          ),
        },
        {
          path: "organization/upload/*",
          element: (
            <ProtectedRoute requireOrgAddVisuals={true}>
              <CreateVisualPage isOrg={true} />
            </ProtectedRoute>
          ),
        },
        {
          path: "organization/info",
          element: (
            <ProtectedRoute requireOrgPermissions={true}>
              <OrganizationInfoPage />
            </ProtectedRoute>
          ),
        },
        {
          path: "organization/join/:encodedName/:joinToken",
          element: <OrganizationJoinPage />,
        },
        { path: "visuals/:slug", element: <TaggedVisualsPage /> },
        { path: "visuals", element: <TaggedVisualsPage /> },
        {
          path: "tags",
          element: (
            <ProtectedRoute>
              <TagListPage />
            </ProtectedRoute>
          ),
        },
        {
          path: "admin/*",
          element: <ProtectedRoute><AdminPage /></ProtectedRoute>,
        },
        // { path: "special/gnydm2023", element: <GNYDM2023Page /> },
        {
          path: "tags/add",
          element: (
            <ProtectedRoute>
              <AddTagsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: "deletedvisuals",
          element: (
            <ProtectedRoute>
              <DeletedVisualsPage />
            </ProtectedRoute>
          ),
        },
        { path: "payment/:newTier", element: <PaymentSuccessPage /> },
        { path: "bundles", element: <BundlePage /> },
        {
          path: "explainer",
          element: (
            <AIFeatureGate featureName="AI Video Generator" featureKey="create_explainers">
              <GenerateExplainerPage />
            </AIFeatureGate>
          ),
        },
        {
          path: "tour",
          element: (
            <AIFeatureGate featureName="AI Video Generator" featureKey="create_explainers">
              <GenerateExplainerPage />
            </AIFeatureGate>
          ),
        },
        { path: "orbit", element: <OrbitDemoPage /> },
        { path: "orbit-pilot", element: <OrbitPilotSignup /> },
        { path: "embed-tests", element: <EmbedTestsPage /> },
        { path: "engagement", element: <EngagementDataExplorer /> },
        { path: "engagement_RU", element: <EngagementDataExplorerRU /> },
        {
          path: "content-dashboard",
          element: (
            <ProtectedRoute>
              <FadeInWrapper><ContentDashboard /></FadeInWrapper>
            </ProtectedRoute>
          ),
        },
        {
          path: "channel-preview",
          element: (
            <ProtectedRoute>
              <ChannelPreview />
            </ProtectedRoute>
          ),
        },
        { path: "*", element: <Navigate to={getDefaultPage(initialUserInfo)} replace /> }, // Catch-all
      ],
    },
    // Standalone route for MyOrbitChannel (no App wrapper - no header/footer)
    {
      path: "/myorbitchannel",
      element: <MyOrbitChannel />
    }
  ];

  const router = createBrowserRouter(routesConfig);

  document.getElementById("loading-image-container").hidden = true;
  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <React.StrictMode>
      <RouterProvider router={router} />
    </React.StrictMode>
  );
};

// Initial fetch of user info
const userInfoSuccessCB = (body) => {
  startReactApp(body);
  if (body?.institution_profile){
    updateInstitutionProfile(body?.email, body?.institution_profile || '');
  }
  if (body?.mission_statement){
    updateMissionStatement(body?.email, body?.mission_statement || '');
  }
};
const userInfoFailureCB = (body) => {
  startReactApp(null); // Start app with userInfo as null if API fails
};

window.addEventListener("DOMContentLoaded", (event) => {
  APIGetUserInfo(userInfoSuccessCB, userInfoFailureCB);
});